/**
 * @fileoverview The annual billing convention (I10).
 *
 * Hans sells a year at TEN months' price — "ten months for twelve" — and says
 * so as *the summer is free*: private teaching runs September to June, so the
 * two months a family does not pay for are the two months nobody teaches.
 *
 * Three things about that sentence are decisions, not phrasing:
 *
 *  1. **It is a multiple, not a discount.** The annual price is ten times the
 *     monthly one. It happens to work out at 16.7%, but nothing computes a
 *     percentage to get there. Before this module the annual figures were
 *     typed by hand next to hand-computed "Save 15%" / "Save 12%" badges, and
 *     they had drifted: $199 beside $19/month is not any convention at all,
 *     and $519 beside $49/month is a third one.
 *
 *  2. **The saving is stated in months, not percent.** "Two months free"
 *     converts better than "save 17%" on identical arithmetic, and the months
 *     are what carries the seasonal framing.
 *
 *  3. **Each client derives from its own source of truth, and the asymmetry is
 *     deliberate.** The web paywall quotes OUR list prices, so it derives the
 *     annual figure from the monthly one by the rule. The iOS paywall quotes
 *     StoreKit's localized prices — which can be a different currency and a
 *     different amount — so it derives the SAVING from the two real prices and
 *     never states a figure Apple did not give it. A hardcoded "$390" beside a
 *     StoreKit "€399" would be a lie in the one place a charge is raised.
 *     The useful consequence: if an App Store product is ever configured at
 *     twelve months' price, the badge disappears rather than advertising a
 *     saving that is not there.
 *
 * Mirrored in `mobile/src/utils/pricing.js` — the two run the same fixture
 * table, so keep them in step or the same plan reads differently on a phone
 * and a laptop. The copy lives here rather than in the components for the same
 * reason.
 */

/** Months charged for a year. The convention, as a multiple. */
export const ANNUAL_MONTHS_CHARGED = 10;

const MONTHS_PER_YEAR = 12;

/** Months a subscriber does not pay for by choosing annual. */
export const ANNUAL_MONTHS_FREE = MONTHS_PER_YEAR - ANNUAL_MONTHS_CHARGED;

/**
 * The seasonal framing. Kept as one string because it is the pitch, not a
 * label: the reason the free months land in summer is the reason a teacher
 * cares.
 */
export const ANNUAL_FRAMING = "Pay for the teaching year — the summer is free.";

/**
 * The annual price implied by a monthly one.
 *
 * @param {number} monthlyCents - Monthly list price in cents.
 * @returns {number|null} Annual price in cents, or null if the input is not a
 *   usable amount.
 */
export function annualPriceFromMonthly(monthlyCents) {
  if (!Number.isFinite(monthlyCents) || monthlyCents <= 0) return null;
  return monthlyCents * ANNUAL_MONTHS_CHARGED;
}

/**
 * Months free implied by two REAL prices, whatever currency they are in.
 *
 * Used where the prices come from the store rather than from us, so the badge
 * describes what will actually be charged. Both amounts must be in the same
 * currency and unit; only their ratio is read.
 *
 * Rounded to whole months because that is how a shopper reads it — a currency
 * whose tiers make the annual 1.9 months cheaper still says "2 months free".
 * The rounding is applied only ABOVE a full month, so a half-month saving is
 * reported as nothing rather than rounded up into a free month nobody is
 * giving away. Rounding down instead would understate the real ten-for-twelve
 * deal everywhere Apple's currency tiers are not exact, which is most of them.
 *
 * Returns null when there is nothing honest to advertise: no saving, an annual
 * price that is not actually cheaper, or unusable inputs.
 *
 * @param {number} monthlyAmount
 * @param {number} annualAmount
 * @returns {number|null}
 */
export function monthsFreeFromPrices(monthlyAmount, annualAmount) {
  if (!Number.isFinite(monthlyAmount) || monthlyAmount <= 0) return null;
  if (!Number.isFinite(annualAmount) || annualAmount <= 0) return null;
  const monthsCharged = annualAmount / monthlyAmount;
  const rawFree = MONTHS_PER_YEAR - monthsCharged;
  if (rawFree < 1) return null;
  const free = Math.round(rawFree);
  // A full year free is arithmetically impossible for a real pair of prices
  // and means one of them is wrong; say nothing rather than promise it.
  if (free >= MONTHS_PER_YEAR) return null;
  return free;
}

/**
 * Badge copy for a months-free figure, e.g. "2 months free".
 *
 * @param {number|null} monthsFree
 * @returns {string|null}
 */
export function monthsFreeLabel(monthsFree) {
  if (!Number.isFinite(monthsFree) || monthsFree < 1) return null;
  return `${monthsFree} month${monthsFree === 1 ? "" : "s"} free`;
}

/**
 * Format an integer-cent amount as a price string, dropping the cents when
 * there are none — every price on the ladder is a whole number of dollars, and
 * "$190" reads as a price where "$190.00" reads as an invoice.
 *
 * @param {number} cents
 * @returns {string}
 */
export function formatPriceFromCents(cents) {
  if (!Number.isFinite(cents)) return "";
  const dollars = cents / 100;
  return Number.isInteger(dollars)
    ? `$${dollars.toLocaleString("en-US")}`
    : `$${dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * The AI-minute top-up (I6): $9 buys 600 minutes, as often as you like.
 *
 * Mirrors `tier_config.TOPUP_PRICE_CENTS` / `TOPUP_MINUTES`, which declare
 * them beside the price ladder rather than inside whichever payment
 * integration happens to sell them — there are two, and they must not be able
 * to disagree about what a top-up is.
 *
 * Deliberately NOT part of the tier tables: a top-up is not a plan. It grants
 * minutes once and changes nothing about what an account is subscribed to,
 * which is exactly the distinction that made `tier_for_price` return null for
 * it on the server.
 */
export const TOPUP_PRICE_CENTS = 900;
export const TOPUP_MINUTES = 600;

/** The wire alias the top-up checkout sends. Resolved to a Stripe price id server-side. */
export const TOPUP_PLAN_ALIAS = "topup_600";

/**
 * How a top-up is described wherever it is offered, e.g. "600 minutes for $9".
 *
 * One helper rather than the phrase typed at each surface: it appears on the
 * usage card, on the paywall and at the moment a recording is refused, and a
 * figure that disagrees with itself between those three is the kind of thing
 * a teacher notices at exactly the wrong time.
 *
 * @returns {string}
 */
export function topupLabel() {
  return `${TOPUP_MINUTES} minutes for ${formatPriceFromCents(TOPUP_PRICE_CENTS)}`;
}

/**
 * Hours a top-up grants, for surfaces that talk in hours.
 *
 * @returns {number}
 */
export function topupHours() {
  return TOPUP_MINUTES / 60;
}
